import { useState } from 'react';
import {
  Page,
  PageBody,
  PageHeader,
  PageHeaderActions,
  PageHeaderIconTile,
  PageHeaderTitle,
} from '@customerio/pluma-components/react';
import { PeopleTable } from '@/features/people';
import { LoadingPage } from '@/components/page/loading';
import { useIsRedacted } from '@/services/permissions/hooks';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { generatePath } from '@/services/router/generate-path';
import { usePeopleListPage, usePeopleSelection, PER_PAGE } from './hooks';
import { usePeopleColumns } from './use-people-columns';
import { AddPeopleButton } from './components/add-people-button';
import { ExportToCsv } from './components/export-to-csv';
import { PeopleBulkActions } from './components/people-bulk-actions';
import { PeopleSearchSlot } from './components/people-search-slot';
import { PeopleWelcome } from './components/people-welcome';

export default function PeopleListPage() {
  const taxonomy = useProductTaxonomy();
  const state = usePeopleListPage();

  // Rows the backend is deleting asynchronously — greyed out until the next
  // load drops them.
  const [pendingDeletionIds, setPendingDeletionIds] = useState<ReadonlySet<string>>(new Set());
  const markRowsPendingDeletion = (ids: readonly string[]) => {
    setPendingDeletionIds((current) => new Set([...current, ...ids]));
  };

  const selection = usePeopleSelection({
    people: state.people,
    totalCount: state.totalCount,
    dataStamp: state.listQuery.dataUpdatedAt,
    pendingDeletionIds,
  });

  const environment = useEnvironmentOptional().data?.environment;
  const columns = usePeopleColumns({
    workspaceId: state.workspaceId,
    sentAttributes: environment?.sent_attributes ?? [],
    isSampleWorkspace: environment?.is_sample_workspace === true,
  });

  const isRedacted = useIsRedacted();
  const shouldRedactCreatedAt = isRedacted && environment?.is_sample_workspace !== true;

  const addPeopleHref = generatePath('/workspaces/:workspaceId/profiles/create', {
    workspaceId: state.workspaceId,
  });

  if (state.welcomeDecision == null) {
    return <LoadingPage />;
  }

  if (state.welcomeDecision === 'welcome') {
    return <PeopleWelcome addPeopleHref={addPeopleHref} />;
  }

  return (
    <Page>
      <PageHeader>
        <PageHeaderIconTile name="person" />
        <PageHeaderTitle>{taxonomy.t('profiles.pageTitleWithFormerName')}</PageHeaderTitle>
        <PageHeaderActions>
          <ExportToCsv
            workspaceId={state.workspaceId}
            isSampleWorkspace={environment?.is_sample_workspace === true}
            sentAttributes={environment?.sent_attributes ?? []}
            hasHiddenResults={state.hasHiddenResults}
            isListLoaded={state.listQuery.isSuccess}
            activeSearchBy={state.activeSearchBy}
            email={state.email}
            filters={state.filters}
          />
          <AddPeopleButton />
        </PageHeaderActions>
      </PageHeader>
      <PageBody>
        <PeopleTable
          people={state.people}
          isLoading={state.listQuery.isFetching}
          allAttributes={columns.allAttributes}
          columnVisibility={columns.columnVisibility}
          onColumnVisibilityChange={columns.onColumnVisibilityChange}
          columnsSettings={columns.columnsSettings}
          pagination={{
            page: state.page,
            pageSize: PER_PAGE,
            totalItemCount: state.totalCount,
            onPageChange: state.setPage,
          }}
          canEdit={state.canEdit}
          rowSelection={selection.rowSelection}
          onRowSelectionChange={selection.onRowSelectionChange}
          allSelected={selection.allSelected}
          totalCount={state.totalCount}
          selectedCount={selection.selectedPeople.length}
          onClearSelection={selection.clearSelection}
          onSelectAll={selection.onSelectAll}
          selectAllDisabled={state.selectAllDisabled}
          noFiltersApplied={state.noFiltersApplied}
          pendingDeletionIds={pendingDeletionIds}
          shouldRedactCreatedAt={shouldRedactCreatedAt}
          hasFilters={state.hasFilters}
          hasHiddenResults={state.hasHiddenResults}
          email={state.email}
          message={state.message}
          searchSlot={
            <PeopleSearchSlot
              activeSearchBy={state.activeSearchBy}
              onActivateSearchBy={state.activateSearchBy}
              email={state.email}
              onSearch={state.search}
              filters={state.filters}
              onApplyFilters={state.applyBuilderFilters}
            />
          }
          addPeopleAction={<AddPeopleButton />}
          actionsSlot={
            <PeopleBulkActions
              workspaceId={state.workspaceId}
              selectedPeople={selection.selectedPeople}
              allSelected={selection.allSelected}
              totalCount={state.totalCount}
              canEdit={state.canEdit}
              email={state.email}
              filters={state.filters}
              noFiltersApplied={state.noFiltersApplied}
              onClearSelection={selection.clearSelection}
              onReload={() => void state.listQuery.refetch()}
              onRowsPendingDeletion={markRowsPendingDeletion}
            />
          }
        />
      </PageBody>
    </Page>
  );
}
